import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2, 
  Users, 
  ShoppingCart,
  Bell,
  Store,
  Loader2,
  Check,
  X,
  Key
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, getAuthHeader, subscription } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Outlet modal state
  const [outletModalOpen, setOutletModalOpen] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [outletForm, setOutletForm] = useState({ name: '', company_reg_no: '', contact_no: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, outletsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/outlets`, { headers }),
        axios.get(`${API}/access-requests`, { headers })
      ]);
      
      setStats(statsRes.data);
      setOutlets(outletsRes.data);
      setAccessRequests(requestsRes.data.filter(r => r.status === 'pending'));
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveOutlet = async () => {
    if (!outletForm.name) {
      toast.error('Please enter outlet name');
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeader();
      if (editingOutlet) {
        await axios.put(`${API}/outlets/${editingOutlet.id}`, outletForm, { headers });
        toast.success('Outlet updated');
      } else {
        await axios.post(`${API}/outlets`, outletForm, { headers });
        toast.success('Outlet created');
      }
      setOutletModalOpen(false);
      setEditingOutlet(null);
      setOutletForm({ name: '', company_reg_no: '', contact_no: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save outlet');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOutlet = async (outletId) => {
    if (!window.confirm('Are you sure you want to delete this outlet? All orders will be deleted.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/outlets/${outletId}`, { headers: getAuthHeader() });
      toast.success('Outlet deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete outlet');
    }
  };

  const handleAccessRequest = async (requestId, action) => {
    try {
      await axios.put(`${API}/access-requests/${requestId}/${action}`, {}, { headers: getAuthHeader() });
      toast.success(`Request ${action}ed`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  const openEditModal = (outlet) => {
    setEditingOutlet(outlet);
    setOutletForm({
      name: outlet.name,
      company_reg_no: outlet.company_reg_no,
      contact_no: outlet.contact_no
    });
    setOutletModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingOutlet(null);
    setOutletForm({ name: '', company_reg_no: '', contact_no: '' });
    setOutletModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl font-bold">Welcome back, {user?.first_name}!</h1>
        <p className="text-muted-foreground mt-1">Manage your delivery operations from here.</p>
      </div>

      {/* Stats Cards */}
      <div className="bento-grid">
        <Card className="metric-card" data-testid="stat-outlets">
          <div className="flex items-center justify-between">
            <Store className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_outlets || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Outlets</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-orders">
          <div className="flex items-center justify-between">
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
            <span className="text-2xl font-bold">{stats?.total_orders || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Total Orders</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-new-orders">
          <div className="flex items-center justify-between">
            <Package className="h-5 w-5 text-blue-500" />
            <span className="text-2xl font-bold text-blue-600">{stats?.new_orders || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">New Orders</p>
        </Card>
        
        <Card className="metric-card" data-testid="stat-requests">
          <div className="flex items-center justify-between">
            <Bell className="h-5 w-5 text-amber-500" />
            <span className="text-2xl font-bold text-amber-600">{stats?.pending_requests || 0}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">Pending Requests</p>
        </Card>
      </div>

      {/* Pending Access Requests */}
      {accessRequests.length > 0 && (
        <Card data-testid="access-requests-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pending Access Requests ({accessRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {accessRequests.map((request) => (
                <div 
                  key={request.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                  data-testid={`request-${request.id}`}
                >
                  <div>
                    <p className="font-medium">{request.user_name}</p>
                    <p className="text-sm text-muted-foreground">{request.user_email}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Requesting access to: {request.outlet_name}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-600 hover:bg-emerald-50"
                      onClick={() => handleAccessRequest(request.id, 'approve')}
                      data-testid={`approve-${request.id}`}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleAccessRequest(request.id, 'reject')}
                      data-testid={`reject-${request.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outlets Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">Your Outlets</h2>
          <Button onClick={openCreateModal} data-testid="add-outlet-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Outlet
          </Button>
        </div>

        {outlets.length === 0 ? (
          <Card className="p-12 text-center">
            <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Outlets Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first outlet to start managing deliveries.</p>
            <Button onClick={openCreateModal}>
              <Plus className="h-4 w-4 mr-2" />
              Create Outlet
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {outlets.map((outlet) => (
              <Card 
                key={outlet.id} 
                className="card-hover cursor-pointer"
                data-testid={`outlet-${outlet.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">{outlet.name}</h3>
                      {outlet.company_reg_no && (
                        <p className="text-sm font-mono text-muted-foreground">
                          #{outlet.company_reg_no}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="icon" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openEditModal(outlet); }}
                        data-testid={`edit-outlet-${outlet.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); handleDeleteOutlet(outlet.id); }}
                        data-testid={`delete-outlet-${outlet.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {outlet.contact_no && (
                    <p className="text-sm text-muted-foreground mb-4">
                      Contact: {outlet.contact_no}
                    </p>
                  )}
                  
                  <Button 
                    className="w-full" 
                    onClick={() => navigate(`/orders/${outlet.id}`)}
                    data-testid={`view-orders-${outlet.id}`}
                  >
                    View Orders
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Outlet Modal */}
      <Dialog open={outletModalOpen} onOpenChange={setOutletModalOpen}>
        <DialogContent data-testid="outlet-modal">
          <DialogHeader>
            <DialogTitle>{editingOutlet ? 'Edit Outlet' : 'Create Outlet'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="outlet-name">Outlet Name *</Label>
              <Input
                id="outlet-name"
                value={outletForm.name}
                onChange={(e) => setOutletForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter outlet name"
                data-testid="outlet-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company-reg">Company Registration No.</Label>
              <Input
                id="company-reg"
                value={outletForm.company_reg_no}
                onChange={(e) => setOutletForm(prev => ({ ...prev, company_reg_no: e.target.value }))}
                placeholder="e.g., ABC123"
                data-testid="outlet-company-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact">Contact Number</Label>
              <Input
                id="contact"
                value={outletForm.contact_no}
                onChange={(e) => setOutletForm(prev => ({ ...prev, contact_no: e.target.value }))}
                placeholder="e.g., +1234567890"
                data-testid="outlet-contact-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutletModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveOutlet} disabled={saving} data-testid="save-outlet-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingOutlet ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
